import React from "react";
import Parse from "parse";

import { useTranslation, AdminLayout } from "@opendash/core";
import { AdminToolbar } from "@opendash/ui";

import {
  Button,
  List,
  Modal,
  Checkbox,
  Tooltip,
  message,
  Space,
  Divider,
} from "antd";

import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@opendash/icons";
import { useParseGet, useParseQuery } from "parse-hooks";
import { Invoice, InvoicePage } from "../types";
import { InvoicePagePDFImport } from "./InvoicePagePDFImport";
import { InvoicePageAnnotation } from "./InvoicePageAnnotation";
import { InvoicePageImageReplace } from "./InvoicePageImageReplace";
import { ParseObjectOrderButtons } from "./ParseObjectOrderButtons";
import { $parse } from "@opendash/plugin-parse";

export const InvoiceEditAdminRoute = React.memo(
  function InvoiceEditAdminRoute() {
    const t = useTranslation();
    const navigate = useNavigate();
    const { invoiceId } = useParams();

    const [isImportVisible, setImportVisible] = React.useState(false);
    const [pageForAnnotation, setPageForAnnotation] =
      React.useState<InvoicePage | null>(null);
    const [pageForImageReplace, setPageForImageReplace] =
      React.useState<InvoicePage | null>(null);

    const invoiceRequest = useParseGet(Invoice, invoiceId as string);

    const [showDeleted, setShowDeleted] = React.useState(false);

    const pageQuery = React.useMemo(() => {
      const query = new Parse.Query(InvoicePage)
        .equalTo("invoice", invoiceRequest?.result as Invoice)
        .descending("order")
        .limit(1000000);

      if (!showDeleted) {
        query.equalTo("deleted", false);
      }

      return query;
    }, [invoiceRequest?.result?.id, showDeleted]);

    const pages = useParseQuery(pageQuery);

    if (!invoiceRequest.result) {
      return null;
    }

    return (
      <AdminLayout contentPadding>
        <AdminToolbar
          title={t("app:admin.invoice_pages.title")}
          description={t("app:admin.invoice_pages.description")}
          onBackButton={() => {
            navigate("/admin/rechnungserklaerer/invoice");
          }}
          actions={[
            <Button
              key="import"
              type="primary"
              icon={<Icon icon="fa:file-pdf" />}
              children={t("app:admin.invoice_page_pdf_import.action")}
              onClick={() => {
                setImportVisible(true);
              }}
            />,
            <Button
              key="new"
              type="primary"
              icon={<Icon icon="fa:plus" />}
              children={t("app:admin.invoice_pages.action_new")}
              onClick={() => {
                const preset = new InvoicePage({
                  tenant: Parse.User.current()?.get("tenant"),
                  invoice: invoiceRequest?.result as Invoice,
                });

                $parse.ui
                  .createObject("APP_InvoicePage", preset)
                  .then((objectId) => {
                    pages.reload();
                  });
              }}
            />,
          ]}
        />
        <div className="od-page-main">
          <List
            loading={pages.loading}
            dataSource={pages.result}
            renderItem={(page) => {
              return (
                <List.Item>
                  <>
                    <List.Item.Meta
                      title={page.get("title")}
                      description={page.get("description")}
                      avatar={
                        <Tooltip
                          overlay={
                            page.get("deleted")
                              ? t(
                                  "app:admin.invoice_pages.visibillity_change_tooltip_deleted"
                                )
                              : page.get("draft")
                              ? t(
                                  "app:admin.invoice_pages.visibillity_change_tooltip_draft"
                                )
                              : t(
                                  "app:admin.invoice_pages.visibillity_change_tooltip_nodraft"
                                )
                          }
                        >
                          <Icon
                            icon={
                              page.get("deleted")
                                ? "fa:trash"
                                : page.get("draft")
                                ? "fa:eye-slash"
                                : "fa:eye"
                            }
                            style={{
                              fontSize: 20,
                              cursor: "pointer",
                              color:
                                page.get("deleted") || page.get("draft")
                                  ? "red"
                                  : undefined,
                            }}
                            onClick={() => {
                              if (page.get("deleted")) {
                              } else if (page.get("draft")) {
                                page.set("draft", false);

                                page
                                  .save()
                                  .then(() => {
                                    message.success(
                                      t(
                                        "app:admin.invoice_pages.visibillity_change_success"
                                      )
                                    );
                                    return pages.reload();
                                  })
                                  .catch(() => {
                                    message.error(
                                      t(
                                        "app:admin.invoice_pages.visibillity_change_error"
                                      )
                                    );
                                  });
                              } else {
                                page.set("draft", true);
                                page
                                  .save()
                                  .then(() => {
                                    message.success(
                                      t(
                                        "app:admin.invoice_pages.visibillity_change_success"
                                      )
                                    );
                                    return pages.reload();
                                  })
                                  .catch(() => {
                                    message.error(
                                      t(
                                        "app:admin.invoice_pages.visibillity_change_error"
                                      )
                                    );
                                  });
                              }
                            }}
                          />
                        </Tooltip>
                      }
                    />

                    <Space direction="horizontal">
                      {!showDeleted && (
                        <ParseObjectOrderButtons
                          objects={pages.result}
                          current={page}
                          onChange={() => {
                            pages.reload();
                          }}
                        />
                      )}

                      <span style={{ display: "inline-block", width: 20 }} />
                      {!showDeleted && (
                        <Button
                          key="edit"
                          type="primary"
                          icon={<Icon icon="fa:edit" />}
                          children={t("app:admin.invoice_pages.action_edit")}
                          onClick={() => {
                            setPageForImageReplace(page);
                          }}
                        />
                      )}

                      {!showDeleted && (
                        <Button
                          key="edit-annotation"
                          type="primary"
                          icon={<Icon icon="fa:marker" />}
                          children={t(
                            "app:admin.invoice_pages.action_annotation"
                          )}
                          onClick={() => {
                            setPageForAnnotation(page);
                          }}
                        />
                      )}

                      {page.get("deleted") && (
                        <Button
                          key="delete-restore"
                          type="default"
                          icon={<Icon icon="fa:trash-undo" />}
                          children={t("app:admin.invoice_pages.action_restore")}
                          onClick={() => {
                            page.set("deleted", false);
                            page.set("draft", true);
                            page.set("order", 100);

                            page.save().then(
                              () => {
                                message.success(
                                  t("app:admin.invoice_pages.restore_success")
                                );

                                pages.reload();
                              },
                              (error) => {
                                message.error(
                                  t("app:admin.invoice_pages.restore_error")
                                );
                              }
                            );
                          }}
                        />
                      )}

                      {page.get("deleted") && (
                        <Button
                          key="delete-final"
                          type="default"
                          danger
                          icon={<Icon icon="fa:trash" />}
                          children={t(
                            "app:admin.invoice_pages.action_delete_final"
                          )}
                          onClick={() => {
                            $parse.ui
                              .deleteObject(page.className, page.id)
                              .then(() => {
                                pages.reload();
                              });
                          }}
                        />
                      )}

                      {!page.get("deleted") && (
                        <Button
                          key="delete"
                          type="default"
                          danger
                          icon={<Icon icon="fa:trash" />}
                          children={t("app:admin.invoice_pages.action_delete")}
                          onClick={() => {
                            Modal.confirm({
                              title: t(
                                "app:admin.invoice_pages.delete_confirm_title"
                              ),
                              content: t(
                                "app:admin.invoice_pages.delete_confirm_message"
                              ),
                              okText: t(
                                "app:admin.invoice_pages.delete_confirm_ok"
                              ),
                              okType: "danger",
                              cancelText: t(
                                "app:admin.invoice_pages.delete_confirm_cancel"
                              ),
                              onOk() {
                                page.set("deleted", true);
                                page.set("order", 0);

                                page.save().then(
                                  () => {
                                    message.success(
                                      t(
                                        "app:admin.invoice_pages.delete_success"
                                      )
                                    );

                                    pages.reload();
                                  },
                                  (error) => {
                                    message.error(
                                      t("app:admin.invoice_pages.delete_error")
                                    );
                                  }
                                );
                              },
                            });
                          }}
                        />
                      )}
                    </Space>
                  </>
                </List.Item>
              );
            }}
          />

          <Divider />

          <Checkbox
            checked={showDeleted}
            onChange={(e) => {
              setShowDeleted(e.target.checked);
            }}
          >
            {t("app:admin.invoice_pages.action_show_deleted")}
          </Checkbox>

          <InvoicePagePDFImport
            visible={isImportVisible}
            onClose={() => setImportVisible(false)}
            invoice={invoiceRequest.result as Invoice}
            onChange={(pageId) => {
              pages.reload();
            }}
          />

          <InvoicePageAnnotation
            page={pageForAnnotation}
            onClose={() => setPageForAnnotation(null)}
            invoice={invoiceRequest.result as Invoice}
            onChange={(pageId) => {
              pages.reload();
            }}
          />

          <InvoicePageImageReplace
            visible={!!pageForImageReplace}
            invoicePage={pageForImageReplace}
            onClose={() => setPageForImageReplace(null)}
            onChange={() => {
              pages.reload();
            }}
          />
        </div>
      </AdminLayout>
    );
  }
);
